import { useState, useEffect } from 'react';
import { NavLink, Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import Consumer_header from "./ConsumerHeader";
import { userService } from "../../_services";
import Signupleft from "../../images/consumer_login.webp";
import mobile_login_bg from "../../images/consumer_login_mobile.webp";
import Cookies from 'universal-cookie';
import CryptoJS from 'crypto-js';

function ConsumerLogIn() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        // Load saved credentials from cookies
        try {
            const cookies = new Cookies();
            const bytes_un = CryptoJS.AES.decrypt(cookies.get('_col_uuid') || '', 'sblw3hn8');
            const bytes_pwd = CryptoJS.AES.decrypt(cookies.get('__gads') || '', 'sblw3hn8');
            const savedUsername = bytes_un.toString(CryptoJS.enc.Utf8);
            const savedPassword = bytes_pwd.toString(CryptoJS.enc.Utf8);
            
            if (savedUsername && savedPassword) {
                setUsername(savedUsername);
                setPassword(savedPassword);
                setRememberMe(true);
            }
        } catch (error) {
            console.error('Error loading saved credentials:', error);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        // Validation
        const newErrors = {};
        if (!username) newErrors.username = 'Username is required';
        if (!password) newErrors.password = 'Password is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSubmitted(true);

        try {
            // PRESERVE EXACT API CALL - DO NOT CHANGE
            const response = await userService.ConsumerLogin(username, password);

            if (response && response.length > 0) {
                if (response[0].code === "1") {
                    // Save credentials if "Remember Me" is checked
                    if (rememberMe) {
                        const cookies = new Cookies();
                        const encryptedUsername = CryptoJS.AES.encrypt(username, 'sblw3hn8').toString();
                        const encryptedPassword = CryptoJS.AES.encrypt(password, 'sblw3hn8').toString();
                        cookies.set('_col_uuid', encryptedUsername, { path: '/', maxAge: 2592000 }); // 30 days
                        cookies.set('__gads', encryptedPassword, { path: '/', maxAge: 2592000 });
                    } else {
                        // Clear cookies
                        const cookies = new Cookies();
                        cookies.remove('_col_uuid', { path: '/' });
                        cookies.remove('__gads', { path: '/' });
                    }

                    // Store user data in localStorage
                    localStorage.setItem('ajs_user_id', response[0].userid);
                    localStorage.setItem('_Host_Pro_type', window.btoa('consumer'));
                    localStorage.setItem('hacker_user_detail', response[0].userdetail);
                    
                    // Redirect
                    window.location.href = '/consumer/summary';
                } else {
                    setErrors({ api: response[0].message || 'Invalid username or password' });
                    setSubmitted(false);
                }
            } else {
                setErrors({ api: 'Login failed. Please try again.' });
                setSubmitted(false);
            }
        } catch (error) {
            console.error('Login error:', error);
            setErrors({ api: 'An error occurred. Please try again.' });
            setSubmitted(false);
        }
    };

    const signUp = () => {
        const currentUrl = window.location.origin;
        if (currentUrl.toLowerCase().includes("localhost:") || 
            currentUrl.toLowerCase().includes("release-backoffice") || 
            currentUrl.toLowerCase().includes("dev-backoffice")) {
            window.open("https://dev-backoffice.nulifespanrx.com", '_blank');
        } else {
            window.open("https://nulifespanrx.com", '_blank');
        }
    };

    // Check if user is already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        const userType = window.atob(localStorage.getItem('_Host_Pro_type'));
        if (userType !== "Admin") {
            if (userType === "consumer") {
                return <Navigate replace to="/consumer/summary" />;
            } else {
                return <Navigate replace to="/summary" />;
            }
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Consumer_header />
            
            <div className="flex-1 flex items-center justify-center p-4 bg-gray-50">
                <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center">
                    {/* Left Side - Image */}
                    <div className="hidden md:block">
                        <img 
                            src={Signupleft} 
                            alt="Consumer Login" 
                            className="w-full h-auto rounded-lg shadow-lg"
                        />
                    </div>

                    {/* Right Side - Login Form */}
                    <Card className="w-full">
                        <CardHeader>
                            <CardTitle className="text-2xl">Welcome Back</CardTitle>
                            <CardDescription>
                                Sign in to your consumer account
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {errors.api && (
                                    <Alert variant="destructive">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{errors.api}</AlertDescription>
                                    </Alert>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="username">Username</Label>
                                    <Input
                                        id="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="Enter your username"
                                        disabled={submitted}
                                        className={errors.username ? 'border-red-500' : ''}
                                    />
                                    {errors.username && (
                                        <p className="text-sm text-red-600">{errors.username}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            disabled={submitted}
                                            className={errors.password ? 'border-red-500' : ''}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <p className="text-sm text-red-600">{errors.password}</p>
                                    )}
                                </div>

                                <div className="flex items-center space-x-2">
                                    <Checkbox
                                        id="remember"
                                        checked={rememberMe}
                                        onCheckedChange={setRememberMe}
                                        disabled={submitted}
                                    />
                                    <Label htmlFor="remember" className="text-sm cursor-pointer">
                                        Remember me
                                    </Label>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    variant="secondary"
                                    disabled={submitted}
                                >
                                    {submitted ? (
                                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Signing in...</>
                                    ) : (
                                        'Sign In'
                                    )}
                                </Button>

                                <div className="text-center text-sm">
                                    <span className="text-gray-600">Don't have an account? </span>
                                    <button
                                        type="button"
                                        onClick={signUp}
                                        className="text-brand-accent hover:underline font-medium"
                                    >
                                        Sign Up here
                                    </button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Mobile Background */}
            <div 
                className="md:hidden fixed inset-0 -z-10 opacity-10"
                style={{ backgroundImage: `url(${mobile_login_bg})`, backgroundSize: 'cover' }}
            />
        </div>
    );
}

export default ConsumerLogIn;
